import { AdType } from './../enums/AdType'
import iconClose from '../../assets/iconClose.svg'
import iconCloseSmall from '../../assets/iconCloseSmall.svg'
import { Device } from '../helpers'
import { addClass, remove, removeClass } from '../helpers/DOM'
import { WindowSize } from '../helpers/windowSize'
import type WebModel from '../models/WebModel'
import { Slot } from './Slot'
import { ITimeoutMap, Timeout } from './helpers/Timeout'
import { hasNative } from '../native/hasNative'
import { hasOutstream } from '../outstream/hasOutstream'
import { Placement } from '../enums/OpenRTB'
import { ADHESION_NATIVE_BODY_CLASS } from '../constants'

const timeouts: RecursivePartial<ITimeoutMap> = {
  desktop: {
    atf: 2300,
    ref: 1200
  },
  mobile: {
    atf: 1000,
    ref: 1000
  }
}

const maxAdhesionOutstreamWidth = 178

@hasNative
@hasOutstream
export class Adhesion extends Slot {
  selector = 'body'
  insertPosition = 'beforeend' as InsertPosition
  slotName = 'adhesion'
  lazy = false
  outstreamPlacement = Placement.floating
  maxOutstreamWidth = maxAdhesionOutstreamWidth
  templateHideMS = 250

  constructor(slotModel: ISlotModel, model: WebModel) {
    super(slotModel, model)
    this.timeout = new Timeout(this, timeouts)

    this._sizes = this.adSizes

    if (Device.isMobile) {
      this.adUnitId = 'adhesion_mobile'
    } else if (Device.isTablet) {
      this.adUnitId = 'adhesion_tablet'
    } else {
      this.adUnitId = 'adhesion_desktop'
    }

    document.body.addEventListener('gumgum-inscreen-close', () => {
      if (this.model.adhesion_close) {
        this.closeAdhesion()
      }
      removeClass(document.body, 'gumgumAdhesion')
    })
  }

  private get adSizes(): AdSizes {
    let sizes: AdSizes = []

    if (this.adUnitId === 'adhesion_mobile') {
      sizes = [
        [320, 50],
        [300, 50],
        [468, 60]
      ]
    } else if (this.adUnitId === 'adhesion_tablet') {
      sizes = [
        [728, 90],
        [468, 60],
        [320, 50],
        [300, 50]
      ]
    } else if (this.adUnitId === 'adhesion_desktop') {
      const MIN_WIDTH_FOR_970_ADS = 1005
      sizes =
        WindowSize.width >= MIN_WIDTH_FOR_970_ADS
          ? [
              [728, 90],
              [970, 90],
              [468, 60]
            ]
          : [
              [728, 90],
              [468, 60]
            ]
    }

    return sizes
  }

  /* eslint-enable no-magic-numbers */
  meetsOutstreamCriteria(): boolean {
    // Outstream Only valid on mobile adhesions
    return Device.isMobileOrTablet && this.model.adhesion_video
  }

  template(): string {
    let template = `
      <div id='${this.wrapperId}' class='adhesion_wrapper' data-wrapper='${this.adUnitId}' data-nosnippet>
        <div id='${this.id}' class='adunit'></div>
        <div class='adhesion_buttons'>`
    if (this.model.adhesion_close) {
      template += `
          <div class='mv_close_button ${this.adUnitId}'>
            <span role='button' aria-label='Close ad' >
              <img class='mv_unbutton' alt="Close ad" id="iconCloseSvg" src=${iconClose} />
              <img class='mv_close_small' alt="Close ad" id="iconCloseSvg" src=${iconCloseSmall} />
            </span>
          </div>
          `
    }

    template += `
        </div>
      </div>
    `

    return template
  }

  cleanup(): void {
    super.cleanup()
    this.closeAdhesion()
  }

  private closeAdhesion(): void {
    const el = this.wrapper
    if (el !== null && this.gSlot !== null) {
      window.googletag.destroySlots([this.gSlot])
      remove(el)
    }
    removeClass(document.body, 'adhesion')
  }

  get adunit(): HTMLElement {
    return this.wrapper.querySelector('.adunit') as HTMLElement
  }

  get adReporterAnchor(): HTMLElement {
    return this.wrapper.querySelector('.adhesion_buttons') as HTMLElement
  }

  get closeButton(): HTMLElement {
    return (
      this.wrapper.querySelector(`.adhesion_buttons`) ||
      document.createElement('div')
    )
  }

  getTemplateTopElements() {
    return [this.adunit, this.closeButton]
  }

  sizeContainersLogic({ height, width } = this): void {
    this.resetSizeClasses()
    addClass(this.wrapper, 'adhesion_container')
    addClass(document.body, 'adhesion')
    addClass(this.wrapper, 'mv-dynamic-size')
    this.sizeIframe()

    if (isStandardAd([width, height]) || this.adType === AdType.prebidNative) {
      this.setupCloseButton()
      if (this.isOutstreamAd()) {
        this.wrapper.style.minHeight = `50px`
        this.wrapper.style.height = `50px`
        this.closeButton.style.height = '50px'
      } else {
        this.wrapper.style.minHeight = `${String(height)}px`
        this.closeButton.style.height = `${String(height)}px`
      }
    } else {
      removeClass(this.wrapper, 'adhesion_container') // Create container
    }

    if (this.adType === AdType.prebidNative) {
      addClass(document.body, ADHESION_NATIVE_BODY_CLASS)
    }
  }

  // If this is a Prebid ad, and the highest Prebid bid was for Outstream, this is an Adhesion Outstream Ad.
  private isOutstreamAd() {
    return (
      this.adType === AdType.prebidStandard && this.highestPrebid?.isOutstream
    )
  }

  private setupCloseButton(): void {
    if (!this.model.adhesion_close) {
      /* no close button! */ return
    }
    // Make Close Button Work
    const closeButton = this.wrapper.querySelector(
      '.mv_close_button'
    ) as HTMLElement
    closeButton.addEventListener('click', () => {
      this.cleanup()

      return false
    })
  }
}

export function isStandardAd(size: AdSize): boolean {
  return size[0] > 1 && size[1] > 1
}
